import type { ReactNode } from 'react';
import {
  CONTENT_PAGES,
  type ContentPageKey,
  type WebsiteContentLinks,
} from '../../utils/websiteSsr/content';
import type { AppConfig, Category } from '../types';
import { getLocalizedEntityField, getLocalizedText } from '../../miniapp/i18n/localization';
import { categoryPath } from '../../utils/websiteSsr/paths';
import {
  DESKTOP_HERO_IMAGE_WIDTHS,
  MOBILE_HERO_IMAGE_WIDTHS,
  responsiveImage,
} from '../../utils/responsiveImages';
import type { ContentBook, ContentSection, SectionKey } from '../content';
import ContentLink from './ContentLink';
import TransitionLink from '../client/TransitionLink';
import PartnershipForm from '../client/PartnershipForm';

export function ContentBody({
  section,
  numbered = false,
}: {
  section: ContentSection;
  numbered?: boolean;
}) {
  return (
    <>
      {section.paragraphs?.map((paragraph) => (
        <p
          key={paragraph}
          className="my-4 max-w-[65ch] text-base leading-8 text-text-secondary md:text-lg"
        >
          {paragraph}
        </p>
      ))}
      {section.items &&
        (numbered ? (
          <ol className="my-6 grid gap-5 pl-6 marker:font-bold marker:text-accent md:grid-cols-2">
            {section.items.map((item) => (
              <li key={item.title} className="pl-2">
                <h3 className="m-0 text-lg font-semibold">{item.title}</h3>
                <p className="mb-0 mt-2 max-w-[65ch] leading-7 text-text-secondary">{item.text}</p>
              </li>
            ))}
          </ol>
        ) : (
          <dl className="my-6 grid gap-x-10 gap-y-6 sm:grid-cols-2">
            {section.items.map((item) => (
              <div key={item.title} className="border-t border-border pt-4">
                <dt className="text-lg font-semibold">{item.title}</dt>
                <dd className="m-0 mt-2 max-w-[65ch] leading-7 text-text-secondary">{item.text}</dd>
              </div>
            ))}
          </dl>
        ))}
      {section.bullets && (
        <ul className="my-5 grid gap-x-8 gap-y-3 pl-5 leading-7 marker:text-accent sm:grid-cols-2">
          {section.bullets.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      )}
    </>
  );
}

export function ContentHero({
  book,
  language,
  settings,
  pathPrefix,
}: {
  book: ContentBook;
  language: string;
  settings?: AppConfig | null;
  pathPrefix: string;
}) {
  const base = pathPrefix.replace(/\/$/, '');
  const customImage = settings?.websiteHeroImageUrl;
  const desktop = responsiveImage(
    customImage || `${base}/assets/branding/banner_desktop.webp`,
    DESKTOP_HERO_IMAGE_WIDTHS,
    '(min-width: 1200px) 1160px, calc(100vw - 40px)',
  );
  const mobile = responsiveImage(
    customImage || `${base}/assets/branding/banner_mobile.webp`,
    MOBILE_HERO_IMAGE_WIDTHS,
    'calc(100vw - 40px)',
  );
  const title =
    getLocalizedText(
      settings?.websiteHeroTitle ?? '',
      settings?.websiteHeroTitleTranslations,
      language,
    ) || book.hero.title;
  const text =
    getLocalizedText(
      settings?.websiteHeroText ?? '',
      settings?.websiteHeroTextTranslations,
      language,
    ) || book.hero.text;
  return (
    <>
      <link
        rel="preload"
        as="image"
        media="(min-width: 768px)"
        imageSrcSet={desktop.srcSet}
        imageSizes={desktop.sizes}
        fetchPriority="high"
      />
      <link
        rel="preload"
        as="image"
        media="(max-width: 767px)"
        imageSrcSet={mobile.srcSet}
        imageSizes={mobile.sizes}
        fetchPriority="high"
      />
      <section className="relative isolate overflow-hidden rounded-4xl bg-secondary">
        <picture>
          <source media="(min-width: 768px)" srcSet={desktop.srcSet} sizes={desktop.sizes} />
          <img
            src={mobile.src}
            srcSet={mobile.srcSet}
            sizes={mobile.sizes}
            width={1600}
            height={640}
            alt=""
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-scrim" />
        <div className="relative px-6 py-12 text-on-scrim sm:px-10 md:py-20 lg:px-16">
          <h1 className="editorial-title m-0 max-w-[16ch] text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          <p className="mt-6 max-w-[42ch] text-xl font-semibold leading-relaxed md:text-2xl">
            {book.hero.subtitle}
          </p>
          <p className="max-w-[55ch] text-base leading-7 md:text-lg">{text}</p>
          <p className="max-w-[55ch] text-sm font-semibold leading-7 md:text-base">
            {book.hero.qualities}
          </p>
          <ContentLink href="/catalog" primary className="mt-4">
            {book.ui.chooseTea}
          </ContentLink>
        </div>
      </section>
      <div className="flex flex-col justify-between gap-6 px-1 md:flex-row md:items-end">
        <p className="m-0 whitespace-pre-line text-lg font-medium leading-8">{book.hero.mission}</p>
        <p className="m-0 text-sm font-semibold tracking-widest text-accent">FROM LEAF TO CUP</p>
      </div>
    </>
  );
}

function SectionActions({
  id,
  book,
  links,
}: {
  id: SectionKey;
  book: ContentBook;
  links: WebsiteContentLinks;
}) {
  const app = links.miniapp ? (
    <>
      <ContentLink href={links.miniapp} primary>
        {book.ui.openApp}
      </ContentLink>
      <p className="m-0 w-full text-sm leading-6 text-text-secondary">{book.ui.appNote}</p>
    </>
  ) : (
    <ContentLink href="/catalog" primary>
      {book.ui.openCatalog}
    </ContentLink>
  );
  const destinations: Partial<Record<SectionKey, [string, string]>> = {
    about: ['/about', book.ui.learnMore],
    regions: ['/articles/tea-regions', book.ui.readMore],
    production: ['/articles/tea-production', book.ui.learnMore],
    history: ['/articles/georgian-tea-history', book.ui.readMore],
    subscription: ['/contacts?topic=subscription', book.ui.subscriptionInquiry],
    gifts: ['/gifts', book.nav.gifts],
    events: ['/events', book.nav.events],
    welcome: ['/tea-club', book.ui.clubInquiry],
    clubPeople: ['/contacts?topic=tea-club', book.ui.clubInquiry],
    corporate: ['#application', book.ui.partnershipInquiry],
    shipping: ['/contacts?topic=delivery', book.ui.contact],
    contactTopics: ['/contacts', book.ui.contact],
    hydrolats: ['/contacts?topic=hydrolats', book.ui.contact],
  };
  const target = destinations[id];
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2">
      {target && <ContentLink href={target[0]}>{target[1]}</ContentLink>}
      {['app', 'clubApp', 'giftApp', 'partnerApp'].includes(id) && app}
      {id === 'gifts' && (
        <>
          <ContentLink href="/catalog?collection=gifts" primary>
            {book.ui.chooseGift}
          </ContentLink>
          <ContentLink href="/articles/hydrolats">{book.collections.hydrolats}</ContentLink>
          <ContentLink href="/contacts?topic=gifts">{book.ui.giftInquiry}</ContentLink>
        </>
      )}
      {id === 'social' && (
        <>
          <p className="m-0 w-full max-w-[65ch] leading-7 text-text-secondary">
            {book.ui.socialText}
          </p>
          {links.telegram && <ContentLink href={links.telegram}>Telegram</ContentLink>}
          {links.instagram && <ContentLink href={links.instagram}>Instagram</ContentLink>}
          {!links.telegram && !links.instagram && (
            <ContentLink href="/contacts">{book.nav.contacts}</ContentLink>
          )}
        </>
      )}
      {id === 'visit' && (
        <ContentLink href="https://www.google.com/maps/search/?api=1&query=Georgia">
          {book.ui.map}
        </ContentLink>
      )}
    </div>
  );
}

function Team({ book, language }: { book: ContentBook; language: string }) {
  const names =
    language === 'ru'
      ? ['Мария Лесная', 'Сергей Fillfree', 'Влад и Катерина', 'Марина Козлова']
      : language === 'ka'
        ? ['მარია ლესნაია', 'სერგეი Fillfree', 'ვლადი და კატერინა', 'მარინა კოზლოვა']
        : ['Maria Lesnaya', 'Sergey Fillfree', 'Vlad & Katerina', 'Marina Kozlova'];
  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold">{book.ui.team}</h3>
      <ul className="m-0 flex list-none flex-wrap gap-x-8 gap-y-3 p-0 text-text-secondary">
        {names.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
    </div>
  );
}

export default function ContentView({
  pageKey,
  book,
  links,
  language,
  pathPrefix,
  settings,
  categories = [],
  children,
}: {
  pageKey: ContentPageKey;
  book: ContentBook;
  links: WebsiteContentLinks;
  language: string;
  pathPrefix: string;
  settings?: AppConfig | null;
  categories?: Category[];
  children?: ReactNode;
}) {
  const page = CONTENT_PAGES.find((entry) => entry.key === pageKey)!;
  const lead = book.sections[page.lead];
  const home = pageKey === 'home';
  const article = 'article' in page && page.article;
  return (
    <div className="grid gap-10 md:gap-16">
      {home ? (
        <>
          <ContentHero
            book={book}
            language={language}
            settings={settings ?? null}
            pathPrefix={pathPrefix}
          />
          <section aria-labelledby="collections-title">
            <h2 id="collections-title" className="m-0 mb-5 text-2xl font-bold md:text-3xl">
              {book.ui.categories}
            </h2>
            <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
              <li>
                <ContentLink href="/catalog" primary>
                  {book.ui.allTea}
                </ContentLink>
              </li>
              {Object.entries(book.collections).map(([key, label]) => (
                <li key={key}>
                  <ContentLink href={`/catalog?collection=${key}`} className="bg-primary">
                    {label}
                  </ContentLink>
                </li>
              ))}
            </ul>
            {categories.length > 0 && (
              <ul className="mt-5 flex list-none flex-wrap gap-x-6 gap-y-3 p-0">
                {categories.map((category) => (
                  <li key={category.id}>
                    <TransitionLink
                      to={categoryPath(category)}
                      className="text-accent underline underline-offset-4"
                    >
                      {getLocalizedEntityField(category, 'name', language)}
                    </TransitionLink>
                  </li>
                ))}
              </ul>
            )}
            <ContentLink href="/catalog" className="mt-5">
              {book.ui.openCatalog}
            </ContentLink>
          </section>
          {children}
        </>
      ) : (
        <>
          <nav aria-label={book.ui.onThisPage} className="text-sm text-text-secondary">
            <ol className="m-0 flex list-none flex-wrap items-center gap-2 p-0">
              <li>
                <TransitionLink to="/" className="text-accent hover:underline">
                  {book.nav.home}
                </TransitionLink>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page">{article ? lead.title : book.nav[page.title]}</li>
            </ol>
          </nav>
          <header className={article ? 'mx-auto w-full max-w-3xl' : 'max-w-4xl'}>
            <h1 className="editorial-title m-0 text-3xl font-bold leading-tight sm:text-4xl md:text-5xl">
              {lead.title}
            </h1>
            <ContentBody section={lead} numbered={pageKey === 'production'} />
            {pageKey === 'about' && <Team book={book} language={language} />}
            {pageKey === 'events' && (
              <>
                <p className="leading-7 text-text-secondary">{book.ui.noEvents}</p>
                <ContentLink href="/contacts?topic=events" primary>
                  {book.ui.contact}
                </ContentLink>
              </>
            )}
            {pageKey === 'gifts' && <SectionActions id="gifts" book={book} links={links} />}
            {pageKey === 'contacts' && (
              <address className="mt-8 grid gap-4 not-italic">
                <a
                  href={`tel:${links.phone}`}
                  className="text-lg font-semibold text-accent underline underline-offset-4"
                >
                  {book.ui.phone}: {links.phone} (Maria)
                </a>
                {links.email && (
                  <a
                    className="text-accent underline underline-offset-4"
                    href={`mailto:${links.email}`}
                  >
                    {links.email}
                  </a>
                )}
                {links.telegram && (
                  <a className="text-accent underline underline-offset-4" href={links.telegram}>
                    Telegram
                  </a>
                )}
                {links.instagram && (
                  <a className="text-accent underline underline-offset-4" href={links.instagram}>
                    Instagram
                  </a>
                )}
              </address>
            )}
          </header>
          {pageKey === 'partnership' && (
            <ContentLink href="#application" primary className="justify-self-start">
              {book.ui.partnershipInquiry}
            </ContentLink>
          )}
          {article && (
            <div className="mx-auto w-full max-w-3xl">
              <ContentLink href="/catalog" primary>
                {book.ui.chooseTea}
              </ContentLink>
              <ContentLink href="/contacts">{book.ui.contact}</ContentLink>
            </div>
          )}
        </>
      )}
      {page.sections.map((key) => {
        const section = book.sections[key];
        const featured = ['subscription', 'welcome'].includes(key);
        const preview =
          home && key === 'history'
            ? { ...section, paragraphs: (section.paragraphs ?? []).slice(0, 3) }
            : section;
        return (
          <section
            key={key}
            id={key}
            aria-labelledby={`${key}-title`}
            className={`scroll-mt-40 ${article ? 'mx-auto w-full max-w-3xl' : ''} ${featured ? 'rounded-3xl bg-primary p-6 md:p-10' : 'border-t border-border pt-8 md:pt-12'}`}
          >
            <h2
              id={`${key}-title`}
              className="editorial-title m-0 max-w-[32ch] text-2xl font-bold leading-snug md:text-3xl"
            >
              {section.title}
            </h2>
            <ContentBody
              section={preview}
              numbered={key === 'production' || key === 'orderSteps'}
            />
            {home && key === 'about' && <Team book={book} language={language} />}
            {key === 'contactTopics' && (
              <ContentLink href={links.email ? `mailto:${links.email}` : `tel:${links.phone}`}>
                {links.email ? book.ui.contact : `${book.ui.phone}: Maria`}
              </ContentLink>
            )}
            {key !== 'contactTopics' && <SectionActions id={key} book={book} links={links} />}
          </section>
        );
      })}
      {pageKey === 'partnership' && (
        <PartnershipForm
          copy={book.form}
          enabled={links.partnershipEnabled}
          phone={links.phone}
          language={language}
        />
      )}
    </div>
  );
}
